import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    LinearProgress,
    makeStyles,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import { i18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';

import LogoBar from '../../../top-nav/LogoBar';
import AdminPage from '../../AdminPage';
import FormSection from '../../../form/FormSection';
import NgpIpAddressField from '../../../form/NgpIpAddressField';
import NgpRow from '../../../form/NgpRow';
import NgpSelect from '../../../form/NgpSelect';
import NgpTextField from '../../../form/NgpTextField';
import { useErrorWrapper } from '../../../../services/FormErrorService';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../../utilities/FormUtilities';
import { useFind } from '../../../../services/FormQueryService';
import {
    calculateEndIp,
    checkIp,
    canAutoconfig,
    countIps,
    DEFAULT_FINDER_VALUES,
    DEFAULT_NETMASK_OPTIONS,
    getProtocol,
    listIps,
    macExists,
    sha1Hex,
} from './nPointFinderHelpers';
import { AGENT_XMS_HOST_FIELD, AGENT_XMS_LOCATION_FIELD } from '../../agent-settings/AgentSettingsConstants';

const scanBlockSize = 100;
const scanBlockDelayMs = 10000;
const socketTimeoutMs = 10000;
const titleText = i18n._(t`Find Hardware nPoints`);
const readApiUrl = '/ipm/v1/admin/agents/globalAgentSettings';

const useStyles = makeStyles((theme) => ({
    panelScrollArea: {
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        paddingRight: theme.spacing(1),

        '& .MuiTableCell-root': {
            fontSize: 13,
            borderBottom: 'none',
        },

        '& .MuiTableCell-head': {
            fontWeight: theme.typography.fontWeightBold,
            backgroundColor: 'transparent',
        },
    },
    content: {
        padding: theme.spacing(3),
    },
    card: {
        padding: theme.spacing(3),
    },
    actionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(8),
        marginTop: theme.spacing(6),
    },
    serverRow: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    serverUrlField: {
        flex: '1 1 320px',
        maxWidth: 320,
    },
    progress: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),

        '&.MuiLinearProgress-root': {
            height: 4,
            borderRadius: 2,
        },
    },
    tableContainer: {
        overflowX: 'auto',
        paddingTop: theme.spacing(2),
    },
    tableCell: {
        maxWidth: 180,
        fontSize: 13,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    versionWarning: {
        marginTop: theme.spacing(1),
        textAlign: 'right',
        color: theme.palette.error.main,
    },
}));

function useFinderSocketService({ onFound, onProgress, onUpdated }) {
    const stateRef = useRef({
        totalIps: 0,
        completeIps: 0,
        timeoutId: null,
        hosts: {},
        sockets: [],
    });

    const clear = () => {
        const currentState = stateRef.current;
        window.clearTimeout(currentState.timeoutId);
        currentState.sockets.forEach((socketEntry) => {
            socketEntry.disconnect?.();
        });
        stateRef.current = {
            totalIps: 0,
            completeIps: 0,
            timeoutId: null,
            hosts: {},
            sockets: [],
        };
    };

    const removeSocket = (host, socket) => {
        const currentState = stateRef.current;
        currentState.sockets = currentState.sockets.filter((entry) => entry.host !== host);
        currentState.hosts[host] = true;
        currentState.completeIps = Object.values(currentState.hosts).filter(Boolean).length;
        if (socket && socket.readyState !== WebSocket.CLOSED) {
            socket.close();
        }

        const progress = currentState.totalIps
            ? parseInt((currentState.completeIps / currentState.totalIps) * 100, 10)
            : 100;
        onProgress(progress);
    };

    const addSocket = (host) => {
        const session = {
            host,
            socket: null,
            timeoutId: null,
            nextAction: '',
            mac: '--',
            gateway: '--',
            uptime: '--',
            version: '--',
            serverUrl: '--',
        };

        const disconnect = () => {
            window.clearTimeout(session.timeoutId);
            if (session.socket && session.socket.readyState !== WebSocket.CLOSED) {
                session.socket.close();
            }
            session.socket = null;
        };

        const sendSignedRequest = async (nonce, callType, parameter = '') => {
            const signature = await sha1Hex(`${parameter}${nonce}`);
            session.socket?.send(
                JSON.stringify({
                    callType,
                    parameter,
                    signature,
                })
            );
        };

        const actions = {
            requestMac: async (message, nonce) => {
                await sendSignedRequest(nonce, 'gtme_web', '');
                session.nextAction = 'requestGateway';
            },
            requestGateway: async (message, nonce) => {
                session.mac =
                    message.data === 'none'
                        ? '--'
                        : message.data
                              .match(/.{1,2}/g)
                              .join(':')
                              .toUpperCase();
                await sendSignedRequest(nonce, 'gate', '');
                session.nextAction = 'requestUptime';
            },
            requestUptime: async (message, nonce) => {
                session.gateway = message.data || '--';
                await sendSignedRequest(nonce, 'up_dhm', '');
                session.nextAction = 'requestUrl';
            },
            requestUrl: async (message, nonce) => {
                session.uptime = message.data || '--';
                await sendSignedRequest(nonce, 'gurl', '');
                session.nextAction = 'requestVersion';
            },
            requestVersion: async (message, nonce) => {
                session.serverUrl = message.data || '--';
                await sendSignedRequest(nonce, 'get_version', '');
                session.nextAction = 'notifyNewNPoint';
            },
            notifyNewNPoint: (message) => {
                session.version = message.data || '------------';
                onFound({
                    ip: host,
                    mac: session.mac,
                    gateway: session.gateway,
                    uptime: session.uptime,
                    url: session.serverUrl,
                    version: session.version,
                    selected: false,
                });
                disconnect();
                session.nextAction = '';
            },
            setUrl: async (message, nonce, url) => {
                await sendSignedRequest(nonce, 'set_server_url', url);
                session.nextAction = 'notifyUrlUpdated';
            },
            notifyUrlUpdated: (message, nonce, url) => {
                onUpdated(host, { url });
                disconnect();
                session.nextAction = '';
            },
        };

        const init = () => {
            session.socket = new WebSocket(`ws://${host}:8000`);
            session.socket.onclose = (event) => {
                window.clearTimeout(session.timeoutId);
                removeSocket(host, event.currentTarget);
            };
            session.timeoutId = window.setTimeout(() => {
                removeSocket(host, session.socket);
            }, socketTimeoutMs);
        };

        const getDeviceInfo = () => {
            init();
            session.nextAction = 'requestMac';
            session.socket.onmessage = async (event) => {
                const message = JSON.parse(event.data);
                const { nonce } = message;
                await actions[session.nextAction]?.(message, nonce);
            };
        };

        const setServerUrl = (url) => {
            init();
            session.nextAction = 'setUrl';
            session.socket.onmessage = async (event) => {
                const message = JSON.parse(event.data);
                const { nonce } = message;
                await actions[session.nextAction]?.(message, nonce, url);
            };
        };

        stateRef.current.sockets.push({ host, disconnect });

        return {
            getDeviceInfo,
            setServerUrl,
        };
    };

    const scanDeviceList = (ipList) => {
        clear();

        const ipBlocks = _.chunk(ipList, scanBlockSize);
        stateRef.current.totalIps = ipList.length;

        const scanBlock = (block) => {
            block.forEach((ipAddress) => {
                stateRef.current.hosts[ipAddress] = false;
                const socketSession = addSocket(ipAddress);
                socketSession.getDeviceInfo();
            });

            if (ipBlocks.length !== 0) {
                stateRef.current.timeoutId = window.setTimeout(() => scanBlock(ipBlocks.shift()), scanBlockDelayMs);
            }
        };

        const firstBlock = ipBlocks.shift();
        if (firstBlock) {
            scanBlock(firstBlock);
        } else {
            onProgress(100);
        }
    };

    const setServerUrls = (ipList, url) => {
        clear();
        stateRef.current.totalIps = ipList.length;

        ipList.forEach((ipAddress) => {
            stateRef.current.hosts[ipAddress] = false;
            const socketSession = addSocket(ipAddress);
            socketSession.setServerUrl(url);
        });
    };

    useEffect(() => clear, []);

    return {
        clear,
        scanDeviceList,
        setServerUrls,
    };
}

export default function NPointFinderPage() {
    const classes = useStyles();
    const [progress, setProgress] = useState(0);
    const [scanInitialized, setScanInitialized] = useState(false);
    const [scanInProgress, setScanInProgress] = useState(false);
    const [devices, setDevices] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { errorWrapper } = useErrorWrapper();

    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: {
            ...DEFAULT_FINDER_VALUES,
            serverUrl: '',
        },
    });
    const { getValues, reset, setValue, watch } = methods;

    useFind({
        url: readApiUrl,
        resetFunc: (response) => {
            reset({
                ...DEFAULT_FINDER_VALUES,
                ...response,
                serverUrl: _.get(response, `${AGENT_XMS_LOCATION_FIELD}[0].${AGENT_XMS_HOST_FIELD}`, ''),
            });
        },
        errorHandler: errorWrapper({}),
    });

    const startIp = watch('startIp');
    const netmask = watch('netmask');
    const serverUrl = watch('serverUrl');
    const endIp = watch('endIp');

    const totalHostCount = useMemo(() => countIps(startIp, endIp), [endIp, startIp]);
    const selectedDevices = useMemo(() => devices.filter((device) => device.selected), [devices]);
    const autoConfigurableDevices = useMemo(() => devices.filter((device) => canAutoconfig(device.version)), [devices]);

    useEffect(() => {
        const nextEndIp = calculateEndIp(startIp, netmask);
        if (nextEndIp && nextEndIp !== getValues('endIp')) {
            setValue('endIp', nextEndIp);
        }
    }, [getValues, netmask, setValue, startIp]);

    useEffect(() => {
        if (String(totalHostCount) !== String(getValues('totalHostCount'))) {
            setValue('totalHostCount', String(totalHostCount));
        }
    }, [getValues, setValue, totalHostCount]);

    const socketService = useFinderSocketService({
        onFound: (device) => {
            setDevices((currentDevices) => {
                if (macExists(currentDevices, device)) {
                    return currentDevices;
                }

                return [...currentDevices, device];
            });
        },
        onProgress: (nextProgress) => {
            setProgress(nextProgress);
            if (nextProgress === 100) {
                setScanInProgress(false);
            }
        },
        onUpdated: (ipAddress, newData) => {
            setDevices((currentDevices) =>
                currentDevices.map((device) => (device.ip === ipAddress ? { ...device, ...newData } : device))
            );
        },
    });

    const inputValid = checkIp(startIp) && checkIp(endIp);
    const allSelected =
        autoConfigurableDevices.length !== 0 && selectedDevices.length === autoConfigurableDevices.length;
    const showVersionWarning = devices.length !== 0 && autoConfigurableDevices.length < devices.length;
    const shouldDisplayProgress = scanInProgress && progress !== 100;

    const updateDeviceSelection = (matcher, isSelected) => {
        setDevices((currentDevices) =>
            currentDevices.map((device) => {
                if (!canAutoconfig(device.version) || !matcher(device)) {
                    return device;
                }

                return {
                    ...device,
                    selected: isSelected,
                };
            })
        );
    };

    const handleToggleAll = (isSelected) => {
        updateDeviceSelection(() => true, isSelected);
    };

    const handleDeviceToggle = (deviceIp, isSelected) => {
        updateDeviceSelection((device) => device.ip === deviceIp, isSelected);
    };

    const handleStartScan = () => {
        setScanInitialized(true);
        setScanInProgress(true);
        setProgress(0);
        setDevices([]);
        socketService.scanDeviceList(listIps(startIp, endIp));
    };

    const handleCancelScan = () => {
        socketService.clear();
        setScanInProgress(false);
    };

    const handleApplyServerUrl = () => {
        const ips = selectedDevices.map((device) => device.ip);
        socketService.setServerUrls(ips, serverUrl);
        handleToggleAll(false);
        setConfirmOpen(false);
    };

    return (
        <div className="flex-container">
            <LogoBar />
            <AdminPage title={titleText} pageId="nPoint-finder" actions={<></>}>
                <div className={classes.panelScrollArea}>
                    <FormProvider {...methods}>
                        <FormSection>
                            <NgpRow>
                                <NgpIpAddressField
                                    fieldName="startIp"
                                    label={i18n._(t`Start IP Address`)}
                                    validateCollectorScope={false}
                                    validateMultiHomed={false}
                                    required
                                />
                                <NgpSelect
                                    fieldName="netmask"
                                    label={i18n._(t`Netmask`)}
                                    required
                                    selectProps={{
                                        MenuProps: {
                                            getContentAnchorEl: null,
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 200,
                                                },
                                            },
                                        },
                                    }}
                                >
                                    {DEFAULT_NETMASK_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.text}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>
                            </NgpRow>
                            <NgpRow>
                                <NgpTextField fieldName="endIp" label={i18n._(t`End IP Address`)} disabled />
                                <NgpTextField
                                    fieldName="totalHostCount"
                                    label={i18n._(t`Total Hosts`)}
                                    disabled
                                    onChangeFunc={_.noop}
                                    inputProps={{ value: totalHostCount }}
                                />
                            </NgpRow>
                            <div className={classes.actionRow}>
                                <Button color="primary" onClick={() => window.close()}>
                                    <Trans>Close</Trans>
                                </Button>
                                <Button color="primary" disabled={!scanInProgress} onClick={handleCancelScan}>
                                    <Trans>Cancel</Trans>
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={scanInProgress || !inputValid}
                                    onClick={handleStartScan}
                                >
                                    <Trans>Scan</Trans>
                                </Button>
                            </div>
                        </FormSection>

                        {scanInitialized && (
                            <FormSection>
                                <Divider />
                                <div className={classes.serverRow}>
                                    <div className={classes.serverUrlField}>
                                        <NgpTextField fieldName="serverUrl" label={i18n._(t`Server URL`)} fullWidth />
                                    </div>
                                    <Button
                                        color="primary"
                                        disabled={selectedDevices.length === 0 || scanInProgress}
                                        onClick={() => setConfirmOpen(true)}
                                    >
                                        {i18n._(t`Apply Server URL to ${selectedDevices.length.toString()} nPoint(s)`)}
                                    </Button>
                                </div>

                                {shouldDisplayProgress && (
                                    <LinearProgress
                                        className={classes.progress}
                                        variant="determinate"
                                        value={progress}
                                    />
                                )}

                                <TableContainer className={classes.tableContainer}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        color="primary"
                                                        checked={allSelected}
                                                        onChange={(event) => handleToggleAll(event.target.checked)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Trans>IP</Trans>
                                                </TableCell>
                                                <TableCell>
                                                    <Trans>MAC</Trans>
                                                </TableCell>
                                                <TableCell>
                                                    <Trans>Gateway</Trans>
                                                </TableCell>
                                                <TableCell>
                                                    <Trans>Uptime</Trans>
                                                </TableCell>
                                                <TableCell>
                                                    <Trans>Version</Trans>
                                                </TableCell>
                                                <TableCell>
                                                    <Trans>Server URL</Trans>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {devices.map((device) => {
                                                const autoConfigurable = canAutoconfig(device.version);

                                                return (
                                                    <TableRow key={device.ip}>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                color="primary"
                                                                disabled={!autoConfigurable}
                                                                checked={Boolean(device.selected)}
                                                                onChange={(event) =>
                                                                    handleDeviceToggle(device.ip, event.target.checked)
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell className={classes.tableCell} title={device.ip}>
                                                            <a
                                                                href={`${getProtocol(device)}${device.ip}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {device.ip}
                                                            </a>
                                                        </TableCell>
                                                        <TableCell className={classes.tableCell} title={device.mac}>
                                                            {device.mac}
                                                        </TableCell>
                                                        <TableCell className={classes.tableCell} title={device.gateway}>
                                                            {device.gateway}
                                                        </TableCell>
                                                        <TableCell className={classes.tableCell} title={device.uptime}>
                                                            {device.uptime}
                                                        </TableCell>
                                                        <TableCell className={classes.tableCell} title={device.version}>
                                                            {device.version}
                                                            {!autoConfigurable && (
                                                                <Typography component="span" color="error">
                                                                    {' '}
                                                                    *
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className={classes.tableCell} title={device.url}>
                                                            {device.url}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {showVersionWarning && (
                                    <div className={classes.versionWarning}>
                                        <Typography variant="caption">
                                            <sup>
                                                <Trans>
                                                    *automatic server URL configuration not available for this nPoint
                                                    version
                                                </Trans>
                                            </sup>
                                        </Typography>
                                    </div>
                                )}
                            </FormSection>
                        )}
                    </FormProvider>
                    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                        <DialogTitle>
                            <Trans>Are you sure?</Trans>
                        </DialogTitle>
                        <DialogContent>
                            <Trans>This will overwrite any existing configuration.</Trans>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setConfirmOpen(false)} color="primary">
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button onClick={handleApplyServerUrl} color="primary">
                                <Trans>OK</Trans>
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            </AdminPage>
        </div>
    );
}
